import { FlaskConical, LogOut, Settings } from "lucide-react";
import { useExperience, type Experience } from "@shared-core/domain/experience";
import { Link, useLocation, useNavigate } from "react-router";
import {
  RECORD_LAYOUT_PARAM,
  useRecordLayoutMode,
  type RecordLayoutMode,
} from "@shared-core/domain/object-ui/record-layout-mode";
import {
  isScopeOverrideRole,
  useScopeOverride,
} from "@shared-core/domain/scopeOverride";
import {
  HOME_PERSONAS,
  homePathFor,
  useHomePersona,
  type HomePersona,
} from "@shared-core/domain/homePersona";
import { roleLabel } from "@shared-core/domain/roleLabels";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { useAuth, useCanUseJourneyTesting } from "../auth/hooks";
import { ToggleGroup, ToggleGroupItem } from "./primitives/toggle-group";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./primitives/dropdown-menu";
import { ScopeOverrideIndicator } from "./ScopeOverrideIndicator";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function AccountMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  // Hooks must run unconditionally even when user is null — they own
  // platform-side state (header provider, persona storage subscription).
  const { showAll, setShowAll } = useScopeOverride({ role: user?.role });
  const { persona, setPersona } = useHomePersona({ role: user?.role });
  const { experience, isOneOffered, setExperience } = useExperience();
  const recordLayout = useRecordLayoutMode();
  const location = useLocation();
  const canUseJourneyTesting = useCanUseJourneyTesting();

  if (!user) return null;

  const isInOne = experience === "one";

  const roleDisplayLabel = roleLabel(user.role);
  const canToggleScope = isScopeOverrideRole(user.role);
  // Only the personas whose home page exists here.
  const reachableHomes = HOME_PERSONAS.filter((p) => isRouteEnabled(p.route));

  return (
    <>
      {canToggleScope && showAll && (
        <ScopeOverrideIndicator onDismiss={() => setShowAll(false)} />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          className="flex items-center justify-center size-9 rounded-full bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
          aria-label={`Account menu for ${user.name}`}
        >
          {initials(user.name)}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium leading-none">{user.name}</p>
              <p className="text-xs text-muted-foreground leading-none">
                {user.email}
              </p>
              <p className="text-xs text-muted-foreground leading-none">
                {roleDisplayLabel}
              </p>
            </div>
          </DropdownMenuLabel>
          {isOneOffered && (
            <>
              <DropdownMenuSeparator />
              {/* Not a menu item, so choosing leaves the menu open, as the
                  scope toggle does: the person sees the switch move. */}
              <div className="px-2 py-1.5">
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  aria-label="Experience"
                  className="w-full"
                  value={experience}
                  onValueChange={(value) => {
                    // Clicking the one already on reports an empty value.
                    if (!value) return;
                    const next = value as Experience;
                    setExperience(next);
                    navigate(
                      next === "classic" ? homePathFor(persona) : paths.root(),
                    );
                  }}
                >
                  <ToggleGroupItem value="classic">Classic</ToggleGroupItem>
                  <ToggleGroupItem value="one">One</ToggleGroupItem>
                </ToggleGroup>
              </div>
            </>
          )}
          {isInOne && recordLayout.isSwitchOffered && (
            <>
              <DropdownMenuSeparator />
              {/* The record-layout mock-up's switch (mock app only): today's record page against the proposed tabs. */}
              <div className="space-y-1 px-2 py-1.5">
                <p className="text-xs text-muted-foreground">Record layout</p>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  aria-label="Record layout"
                  className="w-full"
                  value={recordLayout.stored ?? recordLayout.fallback}
                  onValueChange={(value) => {
                    if (!value) return;
                    recordLayout.setStored(value as RecordLayoutMode);
                    // A link's choice would otherwise win over the one just made.
                    const search = new URLSearchParams(location.search);
                    if (search.has(RECORD_LAYOUT_PARAM)) {
                      search.delete(RECORD_LAYOUT_PARAM);
                      const rest = search.toString();
                      navigate(
                        `${location.pathname}${rest ? `?${rest}` : ""}`,
                        { replace: true },
                      );
                    }
                  }}
                >
                  <ToggleGroupItem value="today">Today</ToggleGroupItem>
                  <ToggleGroupItem value="tabs">Tabs</ToggleGroupItem>
                </ToggleGroup>
              </div>
            </>
          )}
          {canToggleScope && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={showAll}
                onCheckedChange={(next) => setShowAll(next === true)}
                // Stay open: this is a persistent mode switch, not a
                // one-shot command — Radix's default is to dismiss the
                // menu on any item select, which would make the toggle
                // vanish the instant it's used. The visible checkbox
                // state (via `checked`) is the user's confirmation.
                onSelect={(event) => event.preventDefault()}
              >
                Show all customers
              </DropdownMenuCheckboxItem>
            </>
          )}
          {/* With only one home reachable there is nothing to choose between,
              so the whole section goes — picking a persona that lands on the
              not-found page is worse than not offering the choice. */}
          {!isInOne && reachableHomes.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Home view
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={persona}
                onValueChange={(value) => {
                  const next = value as HomePersona;
                  setPersona(next);
                  navigate(homePathFor(next));
                }}
              >
                {reachableHomes.map((home) => (
                  <DropdownMenuRadioItem
                    key={home.persona}
                    value={home.persona}
                  >
                    {home.label} view
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={paths.settingsHome()}>
              <Settings className="size-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
          {isInOne &&
            canUseJourneyTesting &&
            isRouteEnabled("journeyTesting") && (
              // Classic keeps the test console in its Workspace menu; One has
              // no such menu, so it lives here with Settings (brief §11).
              <DropdownMenuItem asChild>
                <Link to={paths.journeyTestingHome()}>
                  <FlaskConical className="size-4" />
                  <span>Test console</span>
                </Link>
              </DropdownMenuItem>
            )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => signOut()}>
            <LogOut className="size-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
